import { type AlertColor } from "@mui/material";
import { type FC, type ReactNode } from "react";
import plants from "../../../assets/images/change-request-confirmation.png";
import server from "../../../assets/images/server.jpg";
import { useTypedTranslation } from "../../hooks/useTypedTranslation";
import * as Styled from "./ChangeRequestResultPage.styled";

export type ChangeRequestStatus = "success" | "processing" | "failed";

export type ChangeRequestResultPageProps = {
    status: ChangeRequestStatus;
    isLoading?: boolean;
    /**
     * Compact variant for use inside dialogs - renders only the body (Alert with text),
     * without image or HeadingL title.
     */
    compact?: boolean;
    imageOverride?: ReactNode;
    hideImage?: boolean;
    titleOverride?: string;
    hideTitle?: boolean;
    bodyText?: string;
    bodyVariant?: "plain" | "banner";
    bodySeverity?: AlertColor;
    /**
     * Documents info banner shown below title.
     * - undefined (default): shown for `success` with default text, hidden otherwise.
     * - string: shown with custom text.
     * - null: explicitly hidden.
     */
    documentsBanner?: string | null;
    /**
     * Additional warning block shown below the body.
     */
    attention?: string;
    actionLabel?: string;
    onAction?: () => void;
};

const STATUS_IMAGE: Record<ChangeRequestStatus, string> = {
    success: plants,
    processing: plants,
    failed: server,
};

export const ChangeRequestResultPage: FC<ChangeRequestResultPageProps> = ({
    status,
    isLoading = false,
    compact = false,
    imageOverride,
    hideImage = false,
    titleOverride,
    hideTitle = false,
    bodyText,
    bodyVariant = "plain",
    bodySeverity,
    documentsBanner,
    attention,
    actionLabel,
    onAction,
}) => {
    const { t } = useTypedTranslation();

    if (isLoading) {
        const loadingTitle = titleOverride ?? t("changeRequestResult.loading.title");
        const loadingImageAlt = t("changeRequestResult.loading.imageAlt");
        return (
            <Styled.Wrapper>
                {!hideImage && (
                    <Styled.ImageWrapper>
                        {imageOverride ?? <Styled.DefaultImage src={plants} alt={loadingImageAlt} />}
                    </Styled.ImageWrapper>
                )}
                {!hideTitle && <Styled.Title>{loadingTitle}</Styled.Title>}
                <Styled.LoadingWrapper>
                    <Styled.Spinner size={60} />
                </Styled.LoadingWrapper>
            </Styled.Wrapper>
        );
    }

    const defaultTitle = t(`changeRequestResult.${status}.title` as const);
    const defaultImageAlt = t(`changeRequestResult.${status}.imageAlt` as const);
    const title = titleOverride ?? defaultTitle;

    const defaultBodySeverity: AlertColor = status === "failed" ? "error" : "info";
    const effectiveBodySeverity = bodySeverity ?? defaultBodySeverity;

    const renderBody = () => {
        if (!bodyText) return null;
        if (bodyVariant === "banner") {
            return <Styled.BodyBanner severity={effectiveBodySeverity}>{bodyText}</Styled.BodyBanner>;
        }
        return <Styled.BodyPlain component="p">{bodyText}</Styled.BodyPlain>;
    };

    const shouldShowDocumentsBanner =
        documentsBanner !== null && (documentsBanner !== undefined || status === "success");
    const documentsBannerText = documentsBanner ?? t("changeRequestResult.banner.documentsInfo");

    if (compact) {
        return (
            <Styled.CompactWrapper>
                {bodyText && <Styled.BodyBanner severity={effectiveBodySeverity}>{bodyText}</Styled.BodyBanner>}
                {shouldShowDocumentsBanner && (
                    <Styled.DocumentsBanner severity="info">{documentsBannerText}</Styled.DocumentsBanner>
                )}
                {attention && <Styled.AttentionBlock severity="warning">{attention}</Styled.AttentionBlock>}
                {actionLabel && onAction && (
                    <Styled.ActionWrapper>
                        <Styled.ActionButton component="button" onClick={onAction} variant="contained">
                            {actionLabel}
                        </Styled.ActionButton>
                    </Styled.ActionWrapper>
                )}
            </Styled.CompactWrapper>
        );
    }

    return (
        <Styled.Wrapper>
            {!hideImage && (
                <Styled.ImageWrapper>
                    {imageOverride ?? <Styled.DefaultImage src={STATUS_IMAGE[status]} alt={defaultImageAlt} />}
                </Styled.ImageWrapper>
            )}
            {!hideTitle && <Styled.Title>{title}</Styled.Title>}
            {shouldShowDocumentsBanner && (
                <Styled.DocumentsBanner severity="info">{documentsBannerText}</Styled.DocumentsBanner>
            )}
            {renderBody()}
            {attention && <Styled.AttentionBlock severity="warning">{attention}</Styled.AttentionBlock>}
            {actionLabel && onAction && (
                <Styled.ActionWrapper>
                    <Styled.ActionButton component="button" onClick={onAction} variant="contained">
                        {actionLabel}
                    </Styled.ActionButton>
                </Styled.ActionWrapper>
            )}
        </Styled.Wrapper>
    );
};
